import React from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------
   COMPANY DETAILS (change here if anything changes)
------------------------------------------------------------------- */
const COMPANY = "Guardian Facility Services Ltd";
const COMPANY_NO = "17521897";
const ADDRESS = "78 Compton Avenue, E6 3DP";
const PHONE = "07459 262621";
const PHONE_LINK = "tel:07459262621";
const EMAIL = "guardianfacilityservicesltd@gmail.com";
const EMAIL_LINK = `https://mail.google.com/mail/?view=cm&fs=1&to=${EMAIL}`;
const LAST_UPDATED = "17 October 2026";

const EmailLink = () => (
  <a href={EMAIL_LINK} target="_blank" rel="noopener noreferrer">{EMAIL}</a>
);

/* ------------------------------------------------------------------
   POLICY CONTENT
------------------------------------------------------------------- */
const sections = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          {COMPANY} (“we”, “us”, “our”) is a company registered in England and Wales
          (Company No. {COMPANY_NO}). Our office is at {ADDRESS}. We provide property letting,
          property maintenance and cleaning services to residential and commercial clients.
        </p>
        <p>
          For the purposes of UK data protection law (the UK GDPR and the Data Protection Act 2018),
          we are the “controller” of the personal information described in this policy.
        </p>
      </>
    ),
  },
  {
    id: "information-we-collect",
    title: "Information we collect",
    body: (
      <>
        <p>Depending on how you deal with us, we may collect:</p>
        <ul>
          <li><strong>Enquiry details</strong> you enter in our contact form or send by phone or email: your name, email address, phone number, property type (residential or commercial), the services you are interested in, and your message.</li>
          <li><strong>Client and property details</strong> needed to provide our services, such as the address of the property, access arrangements and details of the work requested.</li>
          <li><strong>Letting and tenancy details</strong> where you use our lettings services, such as identification, employment and income information, references, credit check results, tenancy agreement details, rent payment records and deposit information.</li>
          <li><strong>Communications</strong> between you and us, including emails, calls and messages about your enquiry or service.</li>
          <li><strong>Basic technical information</strong> that your browser sends automatically when you visit any website, such as your IP address and browser type, which may be seen by our hosting and the third-party services listed below.</li>
        </ul>
        <p>We do not knowingly collect personal information from children, and our services are aimed at adults and businesses.</p>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    title: "How we use your information",
    body: (
      <>
        <p>We use your information only where we have a lawful reason to do so:</p>
        <ul>
          <li><strong>To respond to your enquiry and provide a quote</strong> (legitimate interests, and steps you ask us to take before entering a contract).</li>
          <li><strong>To provide the services you have booked</strong>, including letting, maintenance and cleaning (performance of a contract).</li>
          <li><strong>To carry out tenant referencing and checks and to manage tenancies</strong> (performance of a contract and legitimate interests).</li>
          <li><strong>To meet legal obligations</strong>, such as keeping financial records, arranging safety certificates, and protecting tenancy deposits in an approved scheme (legal obligation).</li>
          <li><strong>To keep our business running safely and effectively</strong>, including keeping records, dealing with complaints and protecting against fraud or misuse of our website (legitimate interests).</li>
          <li><strong>To send you marketing</strong> only if you have agreed to it, and you can withdraw that agreement at any time (consent).</li>
        </ul>
        <p>We do not sell your personal information, and we do not use it for automated decision-making that has legal or similarly significant effects on you.</p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Who we share it with",
    body: (
      <>
        <p>We only share personal information when it is necessary, and only with:</p>
        <ul>
          <li><strong>Our staff, engineers, cleaners and subcontractors</strong>, who receive only the details needed to carry out your job (for example the property address and access arrangements).</li>
          <li><strong>Landlords, tenants and managing agents</strong> connected to a property we look after, where needed to carry out the service.</li>
          <li><strong>Referencing, credit-check and identity-check providers</strong> and <strong>tenancy deposit protection schemes</strong>, where you are a tenant or applicant.</li>
          <li><strong>Technology providers</strong> that help us run our website and email, such as email and form-delivery services (see “Cookies and third-party services” below).</li>
          <li><strong>Professional advisers, insurers and accountants</strong>, who are bound by confidentiality.</li>
          <li><strong>Regulators, courts, the police or other authorities</strong>, where we are legally required or permitted to do so.</li>
        </ul>
        <p>
          Some of these providers may process information outside the UK. Where this happens, we
          take steps to make sure your information is protected by appropriate safeguards.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and third-party services",
    body: (
      <>
        <p>
          Our website does not use advertising or analytics cookies of its own. However, some
          features load content from other companies, who may receive your IP address and set their
          own cookies or similar technologies when you use them:
        </p>
        <ul>
          <li><strong>Google Maps</strong>, shown on our Contact page to display our office location.</li>
          <li><strong>Google Fonts</strong>, used to display the website’s typefaces.</li>
          <li><strong>Gmail and email services</strong>, used when you choose to email us or when your contact form message is delivered to us.</li>
        </ul>
        <p>
          These providers have their own privacy policies, and we encourage you to read them. You
          can control or delete cookies through your browser settings.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "How long we keep your information",
    body: (
      <>
        <p>
          We keep personal information only for as long as we need it for the reasons we collected
          it. In general:
        </p>
        <ul>
          <li>Enquiries that do not lead to work are kept for a reasonable period so we can follow up, then deleted.</li>
          <li>Client, tenancy and job records are kept for the duration of our relationship and afterwards for the period needed to meet legal, tax, accounting and insurance requirements.</li>
          <li>Marketing preferences are kept until you withdraw your consent.</li>
        </ul>
      </>
    ),
  },
  {
    id: "security",
    title: "How we protect your information",
    body: (
      <p>
        We use sensible technical and organisational measures to protect personal information from
        loss, misuse and unauthorised access, including limiting access to those who need it.
        No method of sending information over the internet is completely secure, so please avoid
        including sensitive information such as bank details in the contact form.
      </p>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>Under UK data protection law you have the right to:</p>
        <ul>
          <li>ask for a copy of the personal information we hold about you;</li>
          <li>ask us to correct information that is inaccurate or incomplete;</li>
          <li>ask us to delete your information, in certain circumstances;</li>
          <li>ask us to restrict how we use your information, in certain circumstances;</li>
          <li>object to us using your information where we rely on legitimate interests;</li>
          <li>ask for your information in a portable format, where it applies;</li>
          <li>withdraw your consent at any time, where we rely on consent.</li>
        </ul>
        <p>
          To use any of these rights, contact us using the details at the end of this policy. We
          will normally reply within one month. We may need to confirm your identity first.
        </p>
      </>
    ),
  },
  {
    id: "complaints",
    title: "Complaints",
    body: (
      <p>
        If you are unhappy with how we have handled your information, please tell us first so we
        can put things right. You also have the right to complain to the Information
        Commissioner’s Office (ICO), the UK data protection regulator, at{" "}
        <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a> or by
        calling 0303 123 1113.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <p>
        We may update this policy from time to time. The latest version will always be on this page,
        with the date it was last updated shown at the top.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact us",
    body: (
      <>
        <p>If you have any questions about this policy or how we use your information, please contact us:</p>
        <ul className="pv-contact">
          <li><strong>Company:</strong> {COMPANY} (Company No. {COMPANY_NO})</li>
          <li><strong>Address:</strong> {ADDRESS}</li>
          <li><strong>Phone:</strong> <a href={PHONE_LINK}>{PHONE}</a></li>
          <li><strong>Email:</strong> <EmailLink /></li>
        </ul>
      </>
    ),
  },
];

/* ------------------------------------------------------------------
   PAGE CSS (everything for the Privacy page is in this file)
------------------------------------------------------------------- */
const css = `
.pv-banner{background:var(--navy-deep);color:#fff;padding-top:80px;padding-bottom:80px;border-bottom:4px solid var(--gold)}
.pv-banner h1{color:#fff;font-size:clamp(32px,4.6vw,48px);font-weight:800;letter-spacing:-.5px;margin-bottom:12px;animation:pvRise .7s ease-out both}
@keyframes pvRise{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
.pv-banner p{margin:0;color:#C4CFDA;font-size:18px}

.pv-wrap{padding-top:72px;padding-bottom:96px;display:grid;grid-template-columns:280px 1fr;gap:64px;align-items:start}
.pv-toc{position:sticky;top:110px;border:1px solid #E1E6EB;border-radius:6px;padding:24px 22px;background:var(--paper)}
.pv-toc h2{font-size:17px;margin-bottom:14px}
.pv-toc ol{list-style:none;margin:0;padding:0;counter-reset:toc}
.pv-toc li{counter-increment:toc;margin-bottom:2px}
.pv-toc a{display:flex;gap:10px;padding:7px 0;color:var(--muted);text-decoration:none;font-size:15.5px;line-height:1.35}
.pv-toc a::before{content:counter(toc) ".";font-family:var(--font-head);font-weight:600;color:var(--silver);min-width:22px}
.pv-toc a:hover{color:var(--navy)}

.pv-intro{font-size:19px;color:var(--muted);padding-bottom:28px;margin-bottom:12px;border-bottom:1px solid var(--mist)}
.pv-sec{scroll-margin-top:110px;padding-top:30px;counter-increment:sec}
.pv-sec h2{font-size:26px;font-weight:800;display:flex;gap:14px;align-items:baseline}
.pv-sec h2::before{content:counter(sec);font-family:var(--font-head);font-size:15px;font-weight:700;color:var(--navy);background:var(--mist);border:1px solid #D6DDE4;border-radius:50%;width:34px;height:34px;display:inline-grid;place-items:center;flex:0 0 auto;transform:translateY(-2px)}
.pv-content{counter-reset:sec}
.pv-sec p{color:#3A4652;font-size:17.5px}
.pv-sec ul{margin:0 0 1em;padding-left:22px;color:#3A4652;font-size:17.5px}
.pv-sec li{margin-bottom:10px;padding-left:4px}
.pv-sec li::marker{color:var(--gold-dark)}
.pv-sec strong{color:var(--navy)}
.pv-sec a{color:var(--navy);font-weight:600}
.pv-sec a:hover{color:var(--gold-dark)}
.pv-contact{list-style:none;padding:22px 24px!important;background:var(--paper);border:1px solid #E1E6EB;border-left:4px solid var(--gold);border-radius:6px}
.pv-contact li{padding-left:0;margin-bottom:8px}
.pv-contact li:last-child{margin-bottom:0}
.pv-note{margin-top:40px;padding:20px 24px;border-radius:6px;background:var(--mist);color:var(--muted);font-size:16px}
.pv-note a{color:var(--navy);font-weight:600}

@media (max-width:980px){
  .pv-wrap{grid-template-columns:1fr;gap:36px;padding-top:48px;padding-bottom:72px}
  .pv-toc{position:static}
  .pv-banner{padding-top:64px;padding-bottom:64px}
}
`;

/* ------------------------------------------------------------------
   COMPONENT
------------------------------------------------------------------- */
export default function Privacy() {
  return (
    <>
      <style>{css}</style>

      <section className="pv-banner">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p>Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <div className="container pv-wrap">
        <nav className="pv-toc" aria-label="Privacy policy contents">
          <h2>Contents</h2>
          <ol>
            {sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
            ))}
          </ol>
        </nav>

        <article className="pv-content">
          <p className="pv-intro">
            Your privacy matters to us. This policy explains what personal information {COMPANY} collects,
            how we use it, and the choices and rights you have.
          </p>
          {sections.map((s) => (
            <section className="pv-sec" id={s.id} key={s.id}>
              <h2>{s.title}</h2>
              {s.body}
            </section>
          ))}
          <p className="pv-note">
            See also our <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
          </p>
        </article>
      </div>
    </>
  );
}